import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { AddressDocument } from 'models/address';
import { OrderDocument } from 'models/order';
import { ReturnDocument, ReturnState } from 'models/returns';
import { Reason } from '../orders';
import { ProductDocument } from 'models/product';
import { voidLabel } from '../shipengine';
import { getChatDocumentById, sendMessageToChat } from '../messages';
import { ChatMessageInput, chatMessagesSchema } from 'models/chat';
import { logError } from '@util/logError';

const colRef = collection(db, 'returns');

export const getReturns = async ({ state }: { state?: ReturnState }) => {
  if (typeof state === 'undefined') {
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
  } else {
    const snapshot = await getDocs(query(colRef, where('state', '==', state)));
    return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
  }
};

export const getReturnsByOrderId = async (
  orderId: string,
  uid: string,
  field: 'buyer_id' | 'seller_id' = 'buyer_id'
) => {
  const snapshot = await getDocs(
    query(
      colRef,
      where('order_id', '==', orderId),
      ...(uid === process.env.NEXT_PUBLIC_SUPPORT_ID
        ? []
        : [where(field, '==', uid)]),
      ...(field === 'seller_id'
        ? [where('is_warehouse_return', '==', false)]
        : [])
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export const getReturnsByUser = async (
  uid: string,
  field: 'buyer_id' | 'seller_id',
  state?: number
) => {
  const snapshot = await getDocs(
    query(
      colRef,
      where(field, '==', uid),
      ...(typeof state === 'number' ? [where('state', '==', state)] : []),
      ...(field === 'seller_id'
        ? [where('is_warehouse_return', '==', false)]
        : []),
      orderBy('created', 'desc')
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export const getReturnById = async (id: string) => {
  const snapshot = await getDoc(doc(colRef, id));
  return snapshot.data() as ReturnDocument;
};

export const getWarehouseReturns = async (state?: number) => {
  const snapshot = await getDocs(
    query(
      colRef,
      ...(typeof state === 'number' ? [where('state', '==', state)] : []),
      where('is_warehouse_return', '==', true),
      orderBy('created', 'desc')
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export interface InitiateReturnArgs {
  order: OrderDocument;
  products: ProductDocument[];
  reasons: {
    [product_id: string]: string;
  };
}
export async function initiateReturnRequest(args: InitiateReturnArgs) {
  return await httpsCallable<
    InitiateReturnArgs,
    { chat_id: string; return_id: string }
  >(
    functions,
    FirebaseCallable.initiateReturn
  )(args);
}

export const approveReturn = async ({
  id,
  ship_to,
}: {
  id: string;
  ship_to: AddressDocument;
}) => {
  return await httpsCallable(
    functions,
    'v2_approveReturn'
  )({
    id,
    ship_to,
  });
};

export const denyReturn = async ({ id }: { id: string }) => {
  if (window.confirm('Are you sure you want to deny this return?')) {
    await httpsCallable(functions, 'v2_denyReturn')({ id });
    return true;
  } else {
    return false;
  }
};

export const initiateRefund = async ({
  order,
  returnDoc,
  amount,
}: {
  order: OrderDocument;
  returnDoc: ReturnDocument;
  amount: number;
}) => {
  await httpsCallable<{
    reason: Reason;
    order: OrderDocument;
    partial_amount?: number;
    product_ids?: string[];
    amount_charged?: number;
    return_id: string;
  }>(
    functions,
    'v2_refundOrder'
  )({
    order,
    return_id: returnDoc.id,
    reason: 'product_return',
    partial_amount: amount,
    product_ids: [returnDoc.product_id],
  });
  return {
    id: returnDoc.id,
    amount,
  };
};

export const completeReturn = async ({ id }: { id: string }) => {
  await httpsCallable(functions, 'v2_completeReturn')({ id });
};

export const reopenReturn = async ({
  returnDoc,
}: {
  returnDoc: ReturnDocument;
}) => {
  await updateDoc(doc(colRef, returnDoc.id), {
    state: ReturnState.ReturnRequested,
  });
};

export const cancelReturn = async ({
  returnDoc,
}: {
  returnDoc: ReturnDocument;
}) => {
  // Update return state to cancelled
  try {
    await updateDoc(doc(colRef, returnDoc.id), {
      state: ReturnState.ReturnCancelled,
    });
    // Void label
    const promises = [];
    if (returnDoc.label_id) promises.push(voidLabel(returnDoc.label_id));
    // Send message to buyer
    if (returnDoc.chat_id) {
      const chat = await getChatDocumentById(returnDoc.chat_id);
      if (chat) {
        const message = {
          content:
            'This return has been cancelled due to not shipping in our required time period.',
          content_html: `<div>This return has been cancelled due to not shipping in our required time period. <a class='underline href='/dashboard/purchases/${returnDoc.order_id}' target='_blank'>View Order</a></div>`,
          is_auto: true,
          auto_recipient: returnDoc.buyer_id,
          uid: process.env.NEXT_PUBLIC_SUPPORT_ID!,
          created_at: Date.now(),
        } as ChatMessageInput;
        const messageParsed = chatMessagesSchema.parse(message);
        promises.push(sendMessageToChat(messageParsed, chat));
      }
    }
    await Promise.all(promises);
  } catch (error) {
    logError(error);
    alert((error as Error).message);
  }
};

export const getEligibleRefundAmount = ({
  order_id,
  selected_item,
  reason,
}: {
  order_id: string;
  selected_item: string;
  reason: string;
}) => {
  return httpsCallable<
    {
      order_id: string;
      selected_item: string;
      reason: string;
    },
    {
      fee?: number;
      is_eligible: boolean;
      shipping_cost: number;
      total_refund: number;
    }
  >(
    functions,
    'v2_getEligibleRefund'
  )({ order_id, selected_item, reason });
};

export const addReturnNote = async ({
  id,
  note,
}: {
  id: string;
  note: string;
}) => {
  await updateDoc(doc(colRef, id), {
    notes: arrayUnion({ text: note, created: Date.now() }),
  });
};

export const deleteReturnNote = async ({
  id,
  note,
}: {
  id: string;
  note: {
    text: string;
    created: number;
  };
}) => {
  await updateDoc(doc(colRef, id), {
    notes: arrayRemove(note),
  });
};
